import React, { useState, useEffect, useCallback } from "react";
import { Radio, Input, Popover } from "antd";
import { useNavigate, useLocation } from "react-router-dom";
import { SearchOutlined } from "@material-ui/icons";
import { EmptyBox } from "../../../../../../app/assets/icons";
import { DoubleLeftOutlined, FileDoneOutlined } from "@ant-design/icons";
import useAsideStore from "../../AsideStore";
import { useTranslation } from "../../../../../../app/i18n/LanguageContext";
import {
   NO_ACTION_PLAN_ROUTE_ID
} from "../../../../../../app/pages/workspace/actionPlanDashboard/components/utils/assessmentId";

const ACTION_PLAN_BASE_ROUTE = "/workspace/results/action-plan";

// The entry point from the left nav ("/workspace/results/action-plan") carries no
// assessment id yet and the detail route ("/workspace/results/action-plan/<id>")
// carries the selected one - those are the two routes this list drives. Deeper
// routes (".../<id>/view", survey results, ...) belong to the page itself and are
// left untouched.
const resolveActionPlanRoute = (pathname) => {
   const rawPathname = String(pathname || "");
   const normalizedPathname = rawPathname.endsWith("/") ? rawPathname.slice(0, -1) : rawPathname;
   if (normalizedPathname === ACTION_PLAN_BASE_ROUTE) {
      return { isSelectionRoute: true, assessmentId: null };
   }

   const detailRoutePrefix = `${ACTION_PLAN_BASE_ROUTE}/`;
   if (!normalizedPathname.startsWith(detailRoutePrefix)) {
      return { isSelectionRoute: false, assessmentId: null };
   }

   const routeAssessmentId = normalizedPathname.slice(detailRoutePrefix.length);
   return routeAssessmentId && !routeAssessmentId.includes("/")
      ? { isSelectionRoute: true, assessmentId: routeAssessmentId }
      : { isSelectionRoute: false, assessmentId: null };
};

function MenuActionPlanList({ data, kind }) {
   const { t } = useTranslation();
   const [selectedAssessment, setSelectedAssessment] = useState(null);
   const navigate = useNavigate();
   const location = useLocation();
   const [searchQuery, setSearchQuery] = useState("");
   const { collapsedSecodary, setCollapsedSecondary } = useAsideStore();
   const getAssessmentNameById = useCallback(
      (assessmentId) =>
         data?.find((item) => String(item?.assessmentId) === String(assessmentId))
            ?.assessmentName || "",
      [data]
   );

   // Keeps the route pointed at a real assessment. It also runs for a route id the
   // list does not know (the "no-action-plan" placeholder, or an id left over from
   // another workspace), so the page never keeps requesting data for it.
   useEffect(() => {
      if (!(data?.length > 0)) return;

      const { isSelectionRoute, assessmentId: routeAssessmentId } = resolveActionPlanRoute(
         location.pathname
      );
      if (!isSelectionRoute) return;

      const hasAssessmentFromUrl = data.some(
         (item) => String(item?.assessmentId) === String(routeAssessmentId)
      );
      const nextAssessmentId = hasAssessmentFromUrl ? routeAssessmentId : data[0]?.assessmentId;
      if (nextAssessmentId === undefined || nextAssessmentId === null) return;

      setSelectedAssessment(nextAssessmentId);
      localStorage.setItem("selectedAssessmentId", nextAssessmentId);

      const nextAssessmentName = getAssessmentNameById(nextAssessmentId);
      if (nextAssessmentName) {
         localStorage.setItem("selectedAssessmentName", nextAssessmentName);
         localStorage.setItem("selectedActionPlanAssessmentName", nextAssessmentName);
      }

      if (String(routeAssessmentId) !== String(nextAssessmentId)) {
         navigate(`${ACTION_PLAN_BASE_ROUTE}/${nextAssessmentId}`);
      }
   }, [data, location.pathname, navigate, getAssessmentNameById]);

   const handleAssessmentChange = (assessmentId) => {
      setSelectedAssessment(assessmentId);
      localStorage.setItem("selectedAssessmentId", assessmentId);
      const assessmentName = getAssessmentNameById(assessmentId);
      if (assessmentName) {
         localStorage.setItem("selectedAssessmentName", assessmentName);
         localStorage.setItem("selectedActionPlanAssessmentName", assessmentName);
      }

      if (kind === "action-plan") {
         navigate(`${ACTION_PLAN_BASE_ROUTE}/${assessmentId}`);
      }
   };

   useEffect(() => {
      if (selectedAssessment === null || selectedAssessment === undefined || !data?.length) {
         return;
      }

      const assessmentName = getAssessmentNameById(selectedAssessment);
      if (assessmentName) {
         localStorage.setItem("selectedAssessmentName", assessmentName);
         localStorage.setItem("selectedActionPlanAssessmentName", assessmentName);
      }
   }, [selectedAssessment, data, getAssessmentNameById]);

   const clickedCard = (assessmentId) => {
      handleAssessmentChange(assessmentId);
   };

   // Only a list that actually came back empty may send the route to the
   // placeholder id. `data` is null while the request is still in flight, and
   // redirecting then is what left the dashboard asking the API for an
   // assessment called "no-action-plan".
   useEffect(() => {
      if (!Array.isArray(data) || data.length > 0) return;

      const { isSelectionRoute, assessmentId: routeAssessmentId } = resolveActionPlanRoute(
         location.pathname
      );
      if (!isSelectionRoute || routeAssessmentId === NO_ACTION_PLAN_ROUTE_ID) return;

      navigate(`${ACTION_PLAN_BASE_ROUTE}/${NO_ACTION_PLAN_ROUTE_ID}`);
   }, [data, location.pathname, navigate]);

   return (
      <div className="assessments-menu-list">
         <DoubleLeftOutlined
            className={`collapse-button ${collapsedSecodary && "reversed"}`}
            onClick={() => setCollapsedSecondary(!collapsedSecodary)}
         />
         <div className="assessment-action-plan-wrapper">
            <div className="assessment-action-plan-title">{t("menu-item-assessments")}</div>
            <div className="assessment-action-plan-description">
               <div>
                  {t("action-plan-panel-description-text")}
               </div>
               <div>{t("select-one-see-plan-detail-text")}</div>
               <div style={{ padding: "5px 0px 15px" }}>
                  <Input
                     autoComplete="off"
                     placeholder={t("search-for-assessment-placeholder")}
                     value={searchQuery}
                     onChange={(e) => setSearchQuery(e.target.value)}
                     prefix={<SearchOutlined style={{ color: "#a8a8a8" }} />}
                  />
               </div>
            </div>
            <div className="assessment-cards-wrapper">
               {data?.length > 0 ? (
                  <Radio.Group
                     value={selectedAssessment}
                     onChange={(e) => handleAssessmentChange(e.target.value)}
                     className="custom-radio-group"
                  >
                     {data
                        .filter((item) =>
                           item?.assessmentName?.toLowerCase().includes(searchQuery.toLowerCase())
                        )
                        .map((item, index) => {
                           return (
                           <div
                              className={`custom-assessment-card ${
                                 selectedAssessment === item?.assessmentId
                                    ? "activeAssessmentCard"
                                    : ""
                              }`}
                              key={index}
                              onClick={() => clickedCard(item?.assessmentId)}
                           >
                              <div className="custom-card-upper">
                                 <h3 className="assessment-name">{item?.assessmentName}</h3>
                                 <Radio key={item.assessmentId} value={item.assessmentId}></Radio>
                              </div>
                           </div>
                           );
                        })}
                  </Radio.Group>
               ) : (
                  <div
                     style={{
                        display: "flex",
                        flexDirection: "column",
                        backgroundColor: "#fff",
                        height: "100vh",
                        width: "100%",
                        overflow: "hidden",
                        justifyContent: "center",
                        alignItems: "center",
                        padding: "0px 32px 0px",
                        border: "1px solid #B3BCEC",
                        borderRadius: "8px"
                     }}
                  >
                     <img src={EmptyBox} alt="" width={80} height={50} />
                     <p
                        style={{
                           textAlign: "center",
                           color: "#4158CF",
                           fontSize: "16px",
                           fontWeight: 500
                        }}
                     >
                        {t("no-assessments-here-text")}
                     </p>
                  </div>
               )}
            </div>
         </div>
         <div className={`selected-assessment-indicator ${collapsedSecodary && "opened"}`}>
            <FileDoneOutlined />
            {selectedAssessment && (
               <Popover
                  placement="right"
                  classNames={{
                     root: "selected-assessment-popover",
                     content: "selected-assessment-popover"
                  }}
                  color="rgba(0, 0, 0, 0.75)"
                  title={t("selected-assessment-title")}
                  content={
                     data?.find((item) => String(item?.assessmentId) === String(selectedAssessment))
                        ?.assessmentName || t("no-assessment-selected-text")
                  }
               >
                  <div className="number-indicator">{selectedAssessment ? 1 : 0}</div>
               </Popover>
            )}
         </div>
      </div>
   );
}

export default MenuActionPlanList;
